import { applyZoom, renderOverlay } from './rendering.js'

function midpoint(points) {
  const [a, b] = points
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }
}

function distance(points) {
  const [a, b] = points
  return Math.hypot(a.x - b.x, a.y - b.y)
}

function cancelActiveDrag(state) {
  if (!state.dragMode) return
  state.pendingStroke = null
  state.pendingShape = null
  state.dragMode = null
  if (
    state.activePointerId != null &&
    state.overlayEl?.hasPointerCapture?.(state.activePointerId)
  ) {
    state.overlayEl.releasePointerCapture(state.activePointerId)
  }
  state.activePointerId = null
  renderOverlay(state)
  state.notify()
}

function baseline(state) {
  const points = [...state.panPointers.values()].slice(0, 2)
  if (points.length !== 2) return null
  const mid = midpoint(points)
  return { x: mid.x, y: mid.y, distance: distance(points) }
}

function cancelFrame(state) {
  if (state.panFrame == null) return
  cancelAnimationFrame(state.panFrame)
  state.panFrame = null
}

function applyGesture(state) {
  const points = [...state.panPointers.values()].slice(0, 2)
  if (points.length !== 2 || !state.panLast || !state.stageEl) return

  const stage = state.stageEl
  const mid = midpoint(points)
  const dist = distance(points)
  const rect = stage.getBoundingClientRect()
  const localX = mid.x - rect.left
  const localY = mid.y - rect.top

  if (state.panLast.distance > 0 && dist > 0) {
    const ratio = dist / state.panLast.distance
    if (Math.abs(ratio - 1) > 0.005) {
      const previousScale = state.viewScale
      applyZoom(state, state.zoom * ratio)
      const scaled = state.viewScale / previousScale
      if (scaled !== 1) {
        stage.scrollLeft = (stage.scrollLeft + localX) * scaled - localX
        stage.scrollTop = (stage.scrollTop + localY) * scaled - localY
      }
    }
  }

  stage.scrollLeft -= mid.x - state.panLast.x
  stage.scrollTop -= mid.y - state.panLast.y
  state.panLast = { x: mid.x, y: mid.y, distance: dist }
}

export function panPointerDown(state, e) {
  if (e.pointerType === 'mouse') return false
  state.panPointers ??= new Map()
  state.panPointers.set(e.pointerId, { x: e.clientX, y: e.clientY })
  if (!state.stageEl || state.panPointers.size < 2) return false

  cancelActiveDrag(state)
  state.panLast = baseline(state)
  return true
}

export function panPointerMove(state, e) {
  if (!state.panPointers?.has(e.pointerId)) return false
  state.panPointers.set(e.pointerId, { x: e.clientX, y: e.clientY })
  if (state.panPointers.size < 2 || !state.panLast || !state.stageEl) {
    return false
  }

  if (state.panFrame == null) {
    state.panFrame = requestAnimationFrame(() => {
      state.panFrame = null
      applyGesture(state)
    })
  }
  return true
}

export function panPointerUp(state, e) {
  if (!state.panPointers) return
  if (state.panFrame != null) {
    cancelFrame(state)
    applyGesture(state)
  }
  state.panPointers.delete(e.pointerId)
  state.panLast = state.panPointers.size >= 2 ? baseline(state) : null
}

export function resetPan(state) {
  cancelFrame(state)
  state.panPointers?.clear()
  state.panLast = null
}
